import { ICellRendererParams, ICellRendererComp } from 'ag-grid-community'
import { ValidationIssue } from 'api'
import tippy, { Instance, Props } from 'tippy.js'
import { getErrorMessagesByField } from 'model'
import { getTooltipHtml, VALIDATION_TOOLTIP_OPTIONS } from 'view/components/tooltip'
import { RowDataGetter } from 'view/shared/datagrid'
import './StatusCellRenderer.css'

export interface IStatusCellRendererParams extends ICellRendererParams {
  rowDataGetter?: RowDataGetter<any, any>
  getStatusColor(data: any, isAdmin?: boolean): string
  isBorderRadius?: boolean
  disableTooltip?: boolean
  getFieldNameForDisplay?: (fieldName: string) => string
}

export class StatusCellRenderer implements ICellRendererComp {
  private eGui: HTMLDivElement | null = null
  private eTooltip: Instance | null = null

  private getValidationIssues(params: IStatusCellRendererParams): ValidationIssue[] {
    const rowData = params.rowDataGetter ? params.rowDataGetter(params, true) : params.node.data
    const validationIssues: ValidationIssue[] = rowData?.validationIssues ?? []
    return validationIssues
  }

  private refreshContent(element: HTMLElement, params: IStatusCellRendererParams) {
    element.innerHTML = params.valueFormatted || params.value || null
  }

  private refreshColor(element: HTMLElement, params: IStatusCellRendererParams) {
    const rowData = params.rowDataGetter ? params.rowDataGetter(params, true) : params.node.data
    if (rowData) {
      const color = params.getStatusColor(
        rowData,
        params.context.environmentContext ? params.context.environmentContext.isAdmin : undefined
      )
      element.style.borderLeftColor = color
    }
  }

  private refreshTooltip(tooltip: Instance, params: IStatusCellRendererParams) {
    const validationIssues = this.getValidationIssues(params)
    const messages = getErrorMessagesByField(validationIssues, params.getFieldNameForDisplay)

    if (messages.length > 0) {
      tooltip.setContent(getTooltipHtml(messages))
      tooltip.enable()
    } else {
      tooltip.hide()
      tooltip.disable()
    }
  }

  public init(params: IStatusCellRendererParams) {
    this.eGui = document.createElement('div')
    this.eGui.setAttribute('data-test-id', `StatusCellRenderer-${params.valueFormatted ? params.valueFormatted : params.value}`)

    if (params.isBorderRadius) {
      this.eGui.classList.add('status-cell-unbold')
      if (params.node.firstChild) {
        this.eGui.classList.add('status-cell-first-child')
      }
      if (params.node.lastChild) {
        this.eGui.classList.add('status-cell-last-child')
      }
    } else {
      this.eGui.classList.add('status-cell')
    }
    this.eTooltip = tippy(this.eGui, VALIDATION_TOOLTIP_OPTIONS as Props) as Instance
    this.eTooltip.disable()
    this.refresh(params)
  }

  public getGui(): HTMLElement {
    if (this.eGui) {
      return this.eGui
    } else {
      throw new Error('eGui is not set')
    }
  }

  public refresh(params: IStatusCellRendererParams): boolean {
    if (!this.eGui || !this.eTooltip) {
      throw new Error('cell has not been initialised')
    }

    if (!params.node.group || params.rowDataGetter) {
      this.refreshColor(this.eGui, params)
      this.refreshContent(this.eGui, params)
      if (!params.disableTooltip) {
        this.refreshTooltip(this.eTooltip, params)
      }
    }
    return true
  }

  public destroy() {
    if (this.eTooltip) {
      this.eTooltip.destroy()
      this.eTooltip = null
    }
    this.eGui = null
  }
}
